import type {
  AuditEvent,
  CreateOrgRequest,
  CreatePlatformUserRequest,
  OkResponse,
  PlatformHealth,
  PlatformOrg,
  PlatformUser,
  Template,
  UpdateOrgRequest,
  UpdatePlatformUserRequest,
} from '@twinlink/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@web/lib/api';

export const platformKeys = {
  orgs: ['platform', 'orgs'] as const,
  templates: ['platform', 'templates'] as const,
  users: (q: string) => ['platform', 'users', q] as const,
};

export const usePlatformOrgs = (enabled = true) =>
  useQuery({
    queryKey: platformKeys.orgs,
    queryFn: () => api<{ items: PlatformOrg[] }>('/platform/orgs').then((r) => r.items),
    enabled,
  });

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrgRequest) =>
      api<PlatformOrg>('/platform/orgs', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: platformKeys.orgs }),
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrgRequest }) =>
      api<PlatformOrg>(`/platform/orgs/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform'] }),
  });
}

export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<OkResponse>(`/platform/orgs/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: platformKeys.orgs }),
  });
}

export const usePlatformUsers = (q: string) =>
  useQuery({
    queryKey: platformKeys.users(q),
    queryFn: () =>
      api<{ items: PlatformUser[] }>('/platform/users', { query: { q } }).then((r) => r.items),
    placeholderData: (prev) => prev,
  });

export function useCreatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlatformUserRequest) =>
      api<PlatformUser>('/platform/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'users'] }),
  });
}

export function useUpdatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePlatformUserRequest }) =>
      api<PlatformUser>(`/platform/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'users'] }),
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<OkResponse>(`/platform/users/${id}/unlock`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'users'] }),
  });
}

export const usePlatformImpersonate = () =>
  useMutation({
    mutationFn: (body: { orgId: string; userId: string }) =>
      api<OkResponse>('/platform/impersonate', { method: 'POST', body }),
  });

export interface AuditFilters {
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
}

/** Audit log pages, newest first. `basePath` is the org or the platform audit endpoint. */
export const useAuditLog = (basePath: string, filters: AuditFilters) =>
  useInfiniteQuery({
    queryKey: ['audit', basePath, filters],
    queryFn: ({ pageParam }) =>
      api<{ items: AuditEvent[]; nextCursor: string | null }>(basePath, {
        query: { ...filters, cursor: pageParam, limit: 50 },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const usePlatformHealth = () =>
  useQuery({
    queryKey: ['platform', 'health'],
    queryFn: () => api<PlatformHealth>('/platform/health'),
    refetchInterval: 15_000,
  });

/** Built-in (sealed) templates: listed for staff; renamed and deleted by the platform admin. */
export const usePlatformTemplates = () => useQuery({ queryKey: platformKeys.templates, queryFn: () => api<{ items: Template[] }>('/platform/templates').then((r) => r.items) });

export function usePlatformTemplateActions() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: platformKeys.templates });
  return {
    update: useMutation({
      mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string }) => api<Template>(`/platform/templates/${id}`, { method: 'PATCH', body }),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => api<OkResponse>(`/platform/templates/${id}`, { method: 'DELETE' }), onSuccess: done }),
  };
}
